import { useState, useEffect, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'

import { useTRPC, type RouterOutputs } from '@/trpc'
import { updateOrganizationSchema } from '@tandem/schema/organization'

import Spinner from '@tandem/ui/Spinner'
import $form from '@tandem/ui/form.css'
import $anim from '@tandem/ui/anim.css'
import $table from '@tandem/ui/table.css'

import { PencilIcon as Edit2Icon } from 'lucide-react'
import styles from './-styles.css'


type Organization = RouterOutputs['organization']['list'][number]

export const Route = createFileRoute('/(admin)/organizations/')({
  component: OrganizationsPage,
})

function OrganizationsPage() {
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let [editingId, setEditingId] = useState<number | null>(null)

  let { data: organizations, isLoading } = useQuery(trpc.organization.list.queryOptions())

  let updateOrganization = useMutation({
    ...trpc.organization.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.organization.list.queryFilter())
      setEditingId(null)
    },
  })

  if (isLoading) {
    return (
      <div class={styles.container}>
        <Spinner />
      </div>
    )
  }

  return (
    <div class={styles.container}>
      <header class={styles.header}>
        <div class={styles.search}>
          <input type='text' placeholder='Найти' />
        </div>
      </header>
      <table class={clsx('unstyled', $table.table)}>
        <thead>
          <tr>
            <th>Название</th>
            <th>ИНН</th>
            <th>Телефон</th>
            <th>Веб-сайт</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {organizations?.map(org => (
            editingId === org.id ? (
              <OrganizationEditRow
                key={org.id}
                organization={org}
                onCancel={() => setEditingId(null)}
                onSave={async (values) => {
                  await updateOrganization.mutateAsync({
                    id: org.id,
                    phone: values.phone,
                    website: values.website,
                  })
                }}
                isSubmitting={updateOrganization.isPending}
              />
            ) : (
              <tr key={org.id}>
                <td>{org.name}</td>
                <td>{org.inn}</td>
                <td>{org.phone || '—'}</td>
                <td>{org.website || '—'}</td>
                <td>
                  <div class={styles.actions}>
                    <button class='link' onClick={() => setEditingId(org.id)}>
                      <Edit2Icon />
                      Редактировать
                    </button>
                  </div>
                </td>
              </tr>
            )
          ))}
        </tbody>
      </table>
    </div>
  )
}


function OrganizationEditRow({
  organization,
  onCancel,
  onSave,
  isSubmitting,
}: {
  organization: Organization
  onCancel: () => void
  onSave: (values: { phone: string; website: string }) => Promise<void>
  isSubmitting: boolean
}) {
  let schema = updateOrganizationSchema

  let form = useForm({
    defaultValues: {
      phone: organization.phone || '',
      website: organization.website || '',
    },
    validators: {
      onSubmit: schema.omit({ id: true }),
      async onSubmitAsync({ value }) {
        await onSave(value)
      },
    },
    canSubmitWhenInvalid: true,
  })

  let isFormSubmitting = useStore(form.store, ({ isSubmitting }) => isSubmitting)

  let phoneRef = useRef<HTMLInputElement>(null)
  let websiteRef = useRef<HTMLInputElement>(null)

  let phoneErrors = useStore(form.store, (s) => s.fieldMeta.phone?.errors)
  let websiteErrors = useStore(form.store, (s) => s.fieldMeta.website?.errors)

  useEffect(() => {
    let msg = phoneErrors?.map((e) => typeof e === 'string' ? e : e.message).join(', ') || ''
    phoneRef.current?.setCustomValidity(msg)
    if (msg) phoneRef.current?.reportValidity()
  }, [phoneErrors])

  useEffect(() => {
    let msg = websiteErrors?.map((e) => typeof e === 'string' ? e : e.message).join(', ') || ''
    if (msg) msg = 'Введите корректный URL-адрес.'
    websiteRef.current?.setCustomValidity(msg)
    if (msg) websiteRef.current?.reportValidity()
  }, [websiteErrors])

  return (
    <tr class={clsx({ [$anim.loadingOverlay]: isFormSubmitting || isSubmitting })}>
      <td>{organization.name}</td>
      <td>{organization.inn}</td>
      <td>
        <form.Field
          name='phone'
          validators={{ onChange: schema.shape.phone }}
        >
          {(field) => (
            <input
              ref={phoneRef}
              type='text'
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder='Телефон'
            />
          )}
        </form.Field>
      </td>
      <td>
        <form.Field
          name='website'
          validators={{ onChange: schema.shape.website }}
        >
          {(field) => (
            <input
              ref={websiteRef}
              type='text'
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder='Веб-сайт'
            />
          )}
        </form.Field>
      </td>
      <td>
        <div class={styles.actions}>
          <button class='link' onClick={onCancel}>
            Отмена
          </button>
          <button onClick={() => form.handleSubmit()} disabled={isFormSubmitting || isSubmitting}>
            {(isFormSubmitting || isSubmitting) && <Spinner class={$form.icon} />}
            Сохранить
          </button>
        </div>
      </td>
    </tr>
  )
}
